import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, Zap, Crown } from 'lucide-react';
import { Button } from './ui/button';

const PricingSection = () => {
  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Try all features free',
      icon: Sparkles,
      color: 'slate',
      features: [
        'Basic pricing estimates',
        'Limited searches per day',
        'Single device inventory',
        'Community access',
      ],
      cta: 'Download Free',
      ctaLink: 'https://apps.apple.com/app/id6759179016',
      popular: false,
    },
    {
      name: 'Premium Monthly',
      price: '$29.99',
      period: '/month',
      description: 'Full access, cancel anytime',
      icon: Zap,
      color: 'primary',
      features: [
        'Unlimited pricing estimates',
        'AI Scanner & Price Prediction',
        'Full Inventory Management',
        'Repair Hub access',
        'Customer CRM',
        'Deal Finder',
        'Analytics Dashboard',
        'Priority support',
      ],
      cta: 'Start Free Trial',
      ctaLink: 'https://apps.apple.com/app/id6759179016',
      popular: true,
    },
    {
      name: 'Premium Yearly',
      price: '$250',
      period: '/year',
      description: 'Save 30% vs monthly',
      icon: Crown,
      color: 'green',
      features: [
        'Everything in Premium Monthly',
        'Save $110 per year',
        'Priority feature requests',
        'Early access to new features',
        'Dedicated support',
      ],
      cta: 'Get Best Value',
      ctaLink: 'https://apps.apple.com/app/id6759179016',
      popular: false,
      badge: 'Best Value',
    },
  ];

  const colorClasses = {
    slate: 'border-slate-700 bg-slate-800/50',
    primary: 'border-primary bg-primary/10',
    green: 'border-[#10B981] bg-[#10B981]/10',
  };

  const iconColorClasses = {
    slate: 'bg-slate-700 text-slate-300',
    primary: 'bg-primary text-white',
    green: 'bg-[#10B981] text-white',
  };

  return (
    <section id="pricing" className="py-16 sm:py-24 md:py-32 relative" data-testid="pricing-section">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <span className="text-xs sm:text-sm font-mono tracking-widest uppercase text-primary mb-3 sm:mb-4 block">
            Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6">
            Simple, Transparent
            <span className="text-gradient"> Pricing</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
            Start free and upgrade when you're ready. No hidden fees, no surprises.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-2xl border-2 p-6 sm:p-8 ${colorClasses[plan.color]} ${
                plan.popular ? 'ring-2 ring-primary ring-offset-2 ring-offset-[#0F0F1A]' : ''
              }`}
              data-testid={`pricing-card-${plan.name.toLowerCase().replace(' ', '-')}`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-white text-sm font-medium rounded-full">
                  Most Popular
                </div>
              )}

              {/* Best Value Badge */}
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#10B981] text-white text-sm font-medium rounded-full">
                  {plan.badge}
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className={`w-12 h-12 rounded-xl ${iconColorClasses[plan.color]} flex items-center justify-center mx-auto mb-4`}>
                  <plan.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                <p className="text-slate-400 text-sm">{plan.description}</p>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <span className="text-4xl sm:text-5xl font-bold text-white">{plan.price}</span>
                <span className="text-slate-400">{plan.period}</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full ${plan.color === 'primary' ? 'bg-primary/20' : plan.color === 'green' ? 'bg-[#10B981]/20' : 'bg-slate-700'} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Check className={`w-3 h-3 ${plan.color === 'primary' ? 'text-primary' : plan.color === 'green' ? 'text-[#10B981]' : 'text-slate-400'}`} />
                    </div>
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <a
                href={plan.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button
                  className={`w-full py-6 text-base font-medium ${
                    plan.popular
                      ? 'btn-primary'
                      : plan.color === 'green'
                      ? 'bg-[#10B981] hover:bg-[#059669] text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                  }`}
                >
                  {plan.cta}
                </Button>
              </a>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-slate-400 text-sm">
            Available on <strong className="text-white">iPhone</strong> and <strong className="text-white">iPad</strong> • 
            Cancel anytime • 
            <a href="/terms" className="text-primary hover:underline ml-1">Terms apply</a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
